import { DEFAULT_SEED_EMOJI } from './upvalueEmoji'

export type ReactionOption = {
    emoji: string
    label: string
    aliases?: string[]
    keywords?: string[]
}

export const REACTION_OPTIONS: ReactionOption[] = [
    { emoji: '⬆︎', label: 'Upvalue' },
    { emoji: DEFAULT_SEED_EMOJI, label: 'Seed' },
    { emoji: '👍', label: 'Like' },
    { emoji: '👎', label: 'Dislike' },
    { emoji: '❤️', label: 'Love' },
    { emoji: '🔥', label: 'Fire' },
    { emoji: '😂', label: 'Laugh' },
    { emoji: '😄', label: 'Smile' },
    { emoji: '😮', label: 'Wow' },
    { emoji: '😢', label: 'Sad' },
    { emoji: '😡', label: 'Angry' },
    { emoji: '🤔', label: 'Think' },
    { emoji: '😕', label: 'Confused' },
    { emoji: '🤗', label: 'Care' },
    { emoji: '👏', label: 'Applause' },
    { emoji: '🙌', label: 'Celebrate' },
    { emoji: '🎉', label: 'Party' },
    { emoji: '🙏', label: 'Thanks' },
    { emoji: '💡', label: 'Insightful' },
    { emoji: '💯', label: 'Perfect' },
    { emoji: '💪', label: 'Strong' },
    { emoji: '🚀', label: 'Rocket' },
    { emoji: '👀', label: 'Eyes' },
    { emoji: '‼️', label: 'Emphasize' },
    { emoji: '👋', label: 'Wave' },
    { emoji: '😬', label: 'Grimace' },
    { emoji: '🫡', label: 'Salute' },
    { emoji: '💀', label: 'Dead' },
    { emoji: '🤯', label: 'Mind Blown' },
    { emoji: '😭', label: 'Crying' },
    { emoji: '🍿', label: 'Watching' },
]

type EmojiMartRecord = {
    aliases?: string[]
    id?: string
    keywords?: string[]
    name?: string
    native?: string
    skins?: Array<{ native?: string }>
}

type EmojiMartData = {
    categories: Array<{ emojis: string[] }>
    emojis: Record<string, EmojiMartRecord>
}

type EmojiMartModule = typeof import('emoji-mart')

type EmojiMartRuntime = {
    SearchIndex: EmojiMartModule['SearchIndex']
    getEmojiDataFromNative: EmojiMartModule['getEmojiDataFromNative']
}

const MAX_REACTION_SEARCH_RESULTS = 40
const curatedReactionOptionsByEmoji = new Map(
    REACTION_OPTIONS.map(option => [option.emoji, option] as const),
)

let emojiMartDataPromise: Promise<EmojiMartData> | null = null
let emojiMartRuntimePromise: Promise<EmojiMartRuntime> | null = null
let allReactionOptionsPromise: Promise<ReactionOption[]> | null = null

function normalizeSearchTerms(values: Array<string | undefined | null>) {
    const terms = new Set<string>()

    for (const value of values) {
        const normalizedValue = value?.toString().trim()
        if (!normalizedValue) continue

        terms.add(normalizedValue)
    }

    return Array.from(terms)
}

function buildReactionOptionFromEmojiMartRecord(emojiRecord: EmojiMartRecord | null | undefined) {
    const nativeEmoji = emojiRecord?.native || emojiRecord?.skins?.[0]?.native
    if (!nativeEmoji) return null

    const curatedReaction = getCuratedReactionOption(nativeEmoji)
    if (curatedReaction) return curatedReaction

    const aliases = normalizeSearchTerms([emojiRecord?.id, ...(emojiRecord?.aliases || [])])
    const keywords = normalizeSearchTerms(emojiRecord?.keywords || [])

    return {
        emoji: nativeEmoji,
        label: emojiRecord?.name || aliases[0] || nativeEmoji,
        aliases,
        keywords,
    }
}

async function loadEmojiMartData() {
    if (emojiMartDataPromise) {
        return emojiMartDataPromise
    }

    emojiMartDataPromise = import('@emoji-mart/data')
        .then(dataModule => dataModule.default as EmojiMartData)
        .catch(error => {
            emojiMartDataPromise = null
            throw error
        })

    return emojiMartDataPromise
}

async function loadEmojiMartRuntime() {
    if (emojiMartRuntimePromise) {
        return emojiMartRuntimePromise
    }

    emojiMartRuntimePromise = Promise.all([loadEmojiMartData(), import('emoji-mart')])
        .then(async ([data, emojiMart]) => {
            await emojiMart.init({ data }, { caller: 'treechat-upvalue-reactions' })

            return {
                SearchIndex: emojiMart.SearchIndex,
                getEmojiDataFromNative: emojiMart.getEmojiDataFromNative,
            }
        })
        .catch(error => {
            emojiMartRuntimePromise = null
            throw error
        })

    return emojiMartRuntimePromise
}

export function getCuratedReactionOption(emoji?: string | null) {
    if (!emoji) return null

    return curatedReactionOptionsByEmoji.get(emoji) || null
}

export function mergeReactionOptions(
    ...groups: Array<Array<ReactionOption | null | undefined>>
): ReactionOption[] {
    const mergedOptionsByEmoji = new Map<string, ReactionOption>()

    for (const group of groups) {
        for (const option of group) {
            if (!option?.emoji || mergedOptionsByEmoji.has(option.emoji)) continue

            mergedOptionsByEmoji.set(option.emoji, option)
        }
    }

    return Array.from(mergedOptionsByEmoji.values())
}

export async function loadAllReactionOptions() {
    if (allReactionOptionsPromise) {
        return allReactionOptionsPromise
    }

    allReactionOptionsPromise = loadEmojiMartData()
        .then(data =>
            mergeReactionOptions(
                data.categories.flatMap(category =>
                    category.emojis.map(emojiId =>
                        buildReactionOptionFromEmojiMartRecord(data.emojis[emojiId]),
                    ),
                ),
            ),
        )
        .catch(error => {
            allReactionOptionsPromise = null
            throw error
        })

    return allReactionOptionsPromise
}

export async function resolveReactionOption(emoji?: string | null) {
    const normalizedEmoji = emoji?.trim()
    if (!normalizedEmoji) return null

    const curatedReaction = getCuratedReactionOption(normalizedEmoji)
    if (curatedReaction) {
        return curatedReaction
    }

    const { getEmojiDataFromNative } = await loadEmojiMartRuntime()
    const emojiRecord = await getEmojiDataFromNative(normalizedEmoji)

    return buildReactionOptionFromEmojiMartRecord(emojiRecord)
}

export async function searchReactionOptions(query: string) {
    const normalizedQuery = query.trim()
    if (!normalizedQuery) {
        return []
    }

    const { SearchIndex } = await loadEmojiMartRuntime()
    const emojiRecords = await SearchIndex.search(normalizedQuery, {
        maxResults: MAX_REACTION_SEARCH_RESULTS,
        caller: 'treechat-upvalue-reactions',
    })

    return mergeReactionOptions(
        emojiRecords.map((emojiRecord: EmojiMartRecord) =>
            buildReactionOptionFromEmojiMartRecord(emojiRecord),
        ),
    )
}
